import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import yaml from 'js-yaml';

export type SupportedLanguage = 'en' | 'jp';

type DeepPartial<T> = {
	[P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export interface AppContent {
	languages: SupportedLanguage[];
	navbar: Record<SupportedLanguage, {
		brand: string;
		links: { id: string; label: string }[];
	}>;
	hero: {
		socialLinks: Array<{ id: string; url: string; icon: string; label: string }>;
	} & Record<SupportedLanguage, {
		name: string;
		role: string;
		affiliation: string;
		aboutTitle: string;
		aboutDescriptionHtml: string;
		interestsTitle: string;
		interests: string[];
		educationTitle: string;
		education: Array<{ degree: string; school: string; lab?: string; period: string }>;
	}>;
	publications: {
		categories: Record<SupportedLanguage, {
			international: string;
			domestic: string;
			journal: string;
			all: string;
		}>;
		list: Array<{
			id: string;
			type: 'international' | 'domestic' | 'journal';
			title: Record<SupportedLanguage, string>;
			authors: string[];
			conference: Record<SupportedLanguage, string>;
			year: string;
			keywords: Record<SupportedLanguage, string[]>;
			isNew?: boolean;
			links?: Array<{ label: string; url: string }>;
		}>;
	};
	teaching: Record<SupportedLanguage, {
		title: string;
		courses: Array<{
			title: string;
			organization?: string;
			period?: string;
			role?: string;
			status?: string;
			statusLabel?: string;
			description?: string;
			topics?: string[];
			highlights?: string[];
			links?: Array<{ label: string; url: string }>;
		}>;
	}>;
	blog: {
		title: Record<SupportedLanguage, string>;
		list: Array<{
			id: string;
			category: string;
			title: string;
			excerpt: string;
			date: string;
			icon: string;
			tags: string[];
			url: string;
			stats: { likes: number; bookmarks: number };
		}>;
	};
	footer: Record<SupportedLanguage, { copyright: string }>;
}

interface ContentContextType {
	content: AppContent | null;
	isLoading: boolean;
	error: string | null;
}

const ContentContext = createContext<ContentContextType | undefined>(undefined);

export const ContentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [content, setContent] = useState<AppContent | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);

	useEffect(() => {
		const load = async () => {
			try {
				const res = await fetch('/content.yaml', { cache: 'no-cache' });
				if (!res.ok) throw new Error(`Failed to fetch content.yaml: ${res.status}`);
				const text = await res.text();
				const data = yaml.load(text) as DeepPartial<AppContent>;
				setContent(data as AppContent);
			} catch (e: any) {
				setError(e?.message ?? 'Unknown error while loading content');
			} finally {
				setIsLoading(false);
			}
		};
		load();
	}, []);

	const value = useMemo(() => ({ content, isLoading, error }), [content, isLoading, error]);

	return (
		<ContentContext.Provider value={value}>
			{children}
		</ContentContext.Provider>
	);
};

export const useContent = (): ContentContextType => {
	const ctx = useContext(ContentContext);
	if (!ctx) throw new Error('useContent must be used within ContentProvider');
	return ctx;
};
